import { Search } from '@mui/icons-material';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getConversations } from '../../redux/actions/conversationActions';
import { getFolders } from '../../redux/actions/folderActions';
import { selectAppStatus, setDrawerOpen } from '../../redux/reducers/appStatusReducer';
import { setIsLoadingConversations } from '../../redux/reducers/conversationReducer';
import { setIsLoading as setIsLoadingFolders } from '../../redux/reducers/folderReducer';
import {
  selectNavDrawer,
  setConversationsOnly as reducerSetConversationsOnly,
  setSearchQuery,
} from '../../redux/reducers/navDrawerReducer';
import { AppDispatch } from '../../redux/store';
import { StyledDrawerButton } from '../../theme/CustomComponents';
import { AppEnum } from '../../Types/enums';
import DebouncedSearchInput from '../DebouncedSearchInput';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

const DrawerSearch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useAnalytics();
  const { drawerOpen, selectedApp } = useSelector(selectAppStatus);
  const { searchQuery } = useSelector(selectNavDrawer);
  const isIntellio = selectedApp === AppEnum.DEAL_GPT;
  const location = useLocation();
  const [conversationsOnly, setConversationsOnly] = useState<boolean | undefined>(undefined);
  const [searchSelected, setSearchSelected] = useState(false);
  const previousConversationsOnlyRef = useRef<boolean | undefined>(undefined);

  const runSearch = useCallback(
    (value: string, conversationsOnly?: boolean) => {
      dispatch(setSearchQuery(value));
      dispatch(reducerSetConversationsOnly(conversationsOnly));
      const searchConversations = conversationsOnly === undefined || conversationsOnly === true;
      const searchFolders = conversationsOnly === undefined || conversationsOnly === false;

      if (
        searchFolders ||
        previousConversationsOnlyRef.current === false ||
        previousConversationsOnlyRef.current === undefined
      ) {
        dispatch(setIsLoadingFolders(true));
        dispatch(
          getFolders({
            pageAction: 'reset',
            foldersWithProjects: isIntellio,
            searchQuery: searchFolders ? value : undefined,
          })
        );
      }

      if (
        searchConversations ||
        previousConversationsOnlyRef.current === true ||
        previousConversationsOnlyRef.current === undefined
      ) {
        dispatch(setIsLoadingConversations(true));
        dispatch(
          getConversations({
            pageAction: 'reset',
            searchQuery: searchConversations ? value : undefined,
          })
        );
      }

      analytics.event(AnalyticsEvent.WM_NIGEL_SEARCH_FILTERING, undefined, {
        searchFolders,
        searchConversations,
      });
      previousConversationsOnlyRef.current = conversationsOnly;
    },
    [dispatch, isIntellio, analytics]
  );

  if (location.pathname.includes('/search')) {
    return null;
  }

  return (
    <div
      style={{
        borderBottom: '1px solid #C4C4C4',
      }}
    >
      {drawerOpen ? (
        <div style={{ padding: '10px 0px', display: 'flex', alignItems: 'center' }}>
          <DebouncedSearchInput
            value={searchQuery}
            additionalStyles={{ backgroundColor: '#E8EEF8' }}
            valueUpdated={(value) => {
              runSearch(value, conversationsOnly);
            }}
            onFocus={() => setSearchSelected(true)}
            onBlur={() => setSearchSelected(false)}
            shouldFocus={searchSelected}
            filters={[
              {
                label: 'All',
                value: conversationsOnly === undefined,
                onToggle: () => {
                  setConversationsOnly(undefined);
                  runSearch(searchQuery, undefined);
                },
              },
              {
                label: 'Chats',
                value: conversationsOnly === true,
                onToggle: () => {
                  setConversationsOnly(true);
                  runSearch(searchQuery, true);
                },
              },
              {
                label: 'Folders',
                value: conversationsOnly === false,
                onToggle: () => {
                  setConversationsOnly(false);
                  runSearch(searchQuery, false);
                },
              },
            ]}
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: '5px 0px',
            alignItems: drawerOpen ? 'stretch' : 'center',
          }}
        >
          <StyledDrawerButton
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              setSearchSelected(true);
              dispatch(setDrawerOpen(true));
            }}
          >
            <Search sx={{ height: '20px', width: '20px', color: '#070154' }} />
          </StyledDrawerButton>
        </div>
      )}
    </div>
  );
};

export default DrawerSearch;
